import jobManagerClient from '../services/http-client';
import { createModuleQueryKeys } from '../../createModuleQueryKeys';
import { useModuleQuery } from '../../useModuleQuery';
import type {
  JobModuleApplication,
  JobModuleApplicationsListResponse,
  JobModuleApplicationSummary,
  JobModuleJobsResponse,
} from './interfaces';

// Large limit constant for fetching all data and using cache
export const APPLICATIONS_LARGE_LIMIT = 10000;

export const jobModuleKeys = {
  ...createModuleQueryKeys('job-module'),
  applications: (clientId: string | null) => [...jobModuleKeys.all(clientId), 'applications'] as const,
  applicationsList: (clientId: string | null, filters: Record<string, unknown> = {}) =>
    [...jobModuleKeys.applications(clientId), 'list', filters] as const,
  application: (clientId: string | null, id: string) => [...jobModuleKeys.applications(clientId), 'detail', id] as const,
  applicationSummary: (clientId: string | null, id: string) => [...jobModuleKeys.applications(clientId), 'summary', id] as const,
  jobs: (clientId: string | null) => [...jobModuleKeys.all(clientId), 'jobs'] as const,
};

// GET /applications
export const useJobModuleApplicationsQuery = (params?: {
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}) =>
  useModuleQuery({
    queryKey: (clientId) => jobModuleKeys.applicationsList(clientId, params || {}),
    request: () => jobManagerClient.get<JobModuleApplicationsListResponse>('applications', { params }),
    staleTime: 0, // Always refetch to ensure fresh data when switching clients
    refetchOnMount: 'always', // Always refetch when component mounts or query key changes
  });

// GET /applications/{id}
export const useJobModuleApplicationQuery = (id: string | null | undefined) =>
  useModuleQuery({
    queryKey: (clientId) => jobModuleKeys.application(clientId, id || 'empty'),
    request: () => jobManagerClient.get<JobModuleApplication>(`applications/${id}`),
    enabled: !!id,
    staleTime: 0,
  });

// GET /applications/{applicationId}/summary
export const useJobModuleApplicationSummaryQuery = (applicationId: string | null | undefined) =>
  useModuleQuery({
    queryKey: (clientId) => jobModuleKeys.applicationSummary(clientId, applicationId || 'empty'),
    request: () => jobManagerClient.get<JobModuleApplicationSummary>(`applications/${applicationId}/summary`),
    enabled: !!applicationId,
    staleTime: 0,
  });

// GET /jobs
export const useJobModuleJobsQuery = () =>
  useModuleQuery({
    queryKey: jobModuleKeys.jobs,
    request: () => jobManagerClient.get<JobModuleJobsResponse>('jobs'),
    staleTime: 0, // Always refetch to ensure fresh data when switching clients
  });
